﻿<script setup lang="ts">
import { computed, ref } from 'vue'
import type { CategoryRecord, Id } from '../types/catalog'
import AppIcon from './AppIcon.vue'

defineOptions({ name: 'CategoryNode' })

const props = defineProps<{
  node: CategoryRecord
  childrenMap: Map<Id | null, CategoryRecord[]>
  selectedId: Id | null
}>()

const emit = defineEmits<{
  select: [id: Id]
  add: [parentId: Id]
  rename: [category: CategoryRecord]
  remove: [category: CategoryRecord]
}>()

const expanded = ref(true)
const children = computed(() => props.childrenMap.get(props.node.id) ?? [])
</script>

<template>
  <li class="category-node">
    <div
      class="category-row"
      :class="{ 'category-row--selected': selectedId === node.id }"
      :style="{ '--category-depth': String(node.depth) }"
    >
      <button
        v-if="children.length"
        type="button"
        class="category-toggle"
        :aria-label="expanded ? 'Collapse category' : 'Expand category'"
        @click="expanded = !expanded"
      >
        <AppIcon name="chevron" :size="16" :class="{ 'icon-expanded': expanded }" />
      </button>
      <span v-else class="category-toggle-spacer" />
      <button type="button" class="category-main" @click="emit('select', node.id)">
        <AppIcon name="folder" :size="19" />
        <span class="category-name">{{ node.name }}</span>
        <span v-if="node.directImageCount" class="category-count">{{ node.directImageCount }}</span>
      </button>
      <div class="category-actions">
        <button type="button" class="mini-icon-button" aria-label="Add subcategory" @click="emit('add', node.id)">
          <AppIcon name="plus" :size="16" />
        </button>
        <button type="button" class="mini-icon-button" aria-label="Rename category" @click="emit('rename', node)">
          <AppIcon name="edit" :size="15" />
        </button>
        <button type="button" class="mini-icon-button mini-icon-button--danger" aria-label="Delete category" @click="emit('remove', node)">
          <AppIcon name="trash" :size="15" />
        </button>
      </div>
    </div>

    <ul v-if="expanded && children.length" class="category-children">
      <CategoryNode
        v-for="child in children"
        :key="child.id"
        :node="child"
        :children-map="childrenMap"
        :selected-id="selectedId"
        @select="emit('select', $event)"
        @add="emit('add', $event)"
        @rename="emit('rename', $event)"
        @remove="emit('remove', $event)"
      />
    </ul>
  </li>
</template>
